import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { TEST_IDS } from "../constants/testIds";

const INQUIRY_TYPES = [
  "Investment Opportunity",
  "Investor Relations",
  "Media & Press",
  "Careers",
  "General Inquiry",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", interest: "", message: "",
    inquiry_type: "General Inquiry",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setSubmitted(true);
      toast.success("Thank you — our team will be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="eyebrow">Contact</div>
          <h1 className="font-display text-5xl md:text-6xl mt-6 leading-tight">
            Begin a confidential conversation.
          </h1>
          <p className="mt-8 text-body">
            Our team welcomes inquiries from qualified investors, developers, sovereign counterparties,
            and institutional partners. We respond personally, and with discretion.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-6 text-sm">
            <div>
              <div className="eyebrow" style={{ color: "#3399FF" }}>Providence</div>
              <div className="mt-2 text-[#0B1F3A]">United States Headquarters</div>
              <div className="text-[#5F6B7A]">By appointment only</div>
            </div>
            <div>
              <div className="eyebrow" style={{ color: "#3399FF" }}>Beijing</div>
              <div className="mt-2 text-[#0B1F3A]">Asia Operations</div>
              <div className="text-[#5F6B7A]">By appointment only</div>
            </div>
            <div>
              <div className="eyebrow" style={{ color: "#3399FF" }}>General</div>
              <a href="mailto:contact@firstprovidencecapital.com" className="mt-2 block gold-underline text-[#0B1F3A]">
                contact@firstprovidencecapital.com
              </a>
            </div>
            <div>
              <div className="eyebrow" style={{ color: "#3399FF" }}>Investors</div>
              <a href="mailto:ir@firstprovidencecapital.com" className="mt-2 block gold-underline text-[#0B1F3A]">
                ir@firstprovidencecapital.com
              </a>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          {submitted ? (
            <div className="border border-[#D4AF37] bg-white p-12 text-center">
              <div className="eyebrow" style={{ color: "#D4AF37" }}>Received</div>
              <h3 className="font-display text-3xl mt-4 text-[#0B1F3A]">Thank you.</h3>
              <p className="mt-4 text-body">
                A member of our team will follow up with you shortly at the address you provided.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-[#0B1F3A]/10 bg-white p-10 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Full Name" required>
                  <input
                    data-testid={TEST_IDS.contact.name}
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className={inputCx}
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    data-testid={TEST_IDS.contact.email}
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className={inputCx}
                  />
                </Field>
              </div>
              <Field label="Organization">
                <input
                  data-testid={TEST_IDS.contact.org}
                  value={form.organization}
                  onChange={e => setForm({ ...form, organization: e.target.value })}
                  className={inputCx}
                />
              </Field>
              <Field label="Inquiry Type">
                <select
                  value={form.inquiry_type}
                  onChange={e => setForm({ ...form, inquiry_type: e.target.value })}
                  className={inputCx}
                >
                  {INQUIRY_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Area of Interest">
                <input
                  data-testid={TEST_IDS.contact.interest}
                  placeholder="e.g. Energy — LNG Asia; AI infrastructure North America"
                  value={form.interest}
                  onChange={e => setForm({ ...form, interest: e.target.value })}
                  className={inputCx}
                />
              </Field>
              <Field label="Message" required>
                <textarea
                  data-testid={TEST_IDS.contact.message}
                  rows={5}
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  className={`${inputCx} resize-y`}
                />
              </Field>
              <div className="flex items-center justify-between pt-4">
                <div className="text-[10px] text-[#5F6B7A] uppercase tracking-widest">Confidential · Not an offer of securities</div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={TEST_IDS.contact.submit}
                  className="bg-[#0B1F3A] text-[#F8F7F2] px-8 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[#3399FF] disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Submit Inquiry"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCx = "w-full h-11 px-3 bg-white border border-[#0B1F3A]/20 text-[#0B1F3A] text-sm focus:outline-none focus:border-[#3399FF]";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#5F6B7A] mb-2">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </div>
      {children}
    </label>
  );
}
